import { FC, useEffect, useState } from 'react';
import { fetchProductTemplate, ProductTemplate } from '../../api/productTemplates';
import PageHeaderWithBack from '../../components/PageHeaderWithBack';

interface TemplatesDetailPanelProps {
  id: string;
  onBack: () => void;
}

const TemplatesDetailPanel: FC<TemplatesDetailPanelProps> = ({ id, onBack }) => {
  const [data, setData] = useState<ProductTemplate | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    setError(null);

    fetchProductTemplate(id)
      .then((response) => {
        if (isMounted) {
          setData(response);
        }
      })
      .catch((err) => {
        if (isMounted) {
          setError(err instanceof Error ? err.message : 'Failed to load template');
        }
      })
      .finally(() => {
        if (isMounted) {
          setLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [id]);

  if (loading) {
    return (
      <div className="mt-6 flex-1 rounded-2xl bg-white px-6 py-5 text-sm text-slate-500 shadow-inner">
        Loading template...
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="mt-6 flex-1 rounded-2xl bg-white px-6 py-5 text-sm text-danger shadow-inner">
        {error ?? 'Template not found.'}
        <div className="mt-4">
          <button
            type="button"
            className="rounded-xl bg-primary px-4 py-2 text-sm text-white shadow"
            onClick={onBack}
          >
            Back to templates
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 rounded-2xl bg-white px-6 py-5 shadow-inner">

      <PageHeaderWithBack
        title={data.name}
        description={<p className="mt-1 max-w-2xl text-sm text-slate-500">
          {data.description?.length ? data.description : "No description provided."}
        </p>}
        onBack={onBack}
        backLabel="Back to templates"
      />

      <div className="mt-6 grid gap-6 lg:grid-cols-[320px_1fr]">

        <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4 text-sm text-slate-600">
          <h3 className="text-base font-semibold text-slate-700">Metadata</h3>
          <dl className="mt-4 space-y-3 text-sm text-slate-700">
            <div>
              <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">ERP ID</dt>
              <dd>{data.erpId}</dd>
            </div>
            <div>
              <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                Last Updated
              </dt>
              <dd>{new Date(data.updatedAt).toLocaleString()}</dd>
            </div>
            <div>
              <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                Attribute Count
              </dt>
              <dd>{data.attributes?.length ?? 0}</dd>
            </div>
          </dl>
        </div>



        <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4 text-sm text-slate-600">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-base font-semibold text-slate-700">Attributes</h3>
            <span className="text-xs text-slate-400 ml-2">({data.attributes?.length} total)</span>
          </div>
          {data.attributes?.length ? (
            <ul className="space-y-3">
              {data.attributes.map((attribute) => (
                <li key={attribute.id} className="rounded-xl border border-slate-200 bg-white px-4 py-3">
                  <div className="text-sm font-semibold text-slate-700">
                    {attribute.name} <span className="text-xs text-slate-400">(ERP {attribute.erpId})</span>
                  </div>
                  <div className="mt-1 text-xs text-slate-500">
                    Default: {attribute.defaultValue?.length ? attribute.defaultValue : '—'}
                  </div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {attribute.options?.length ? (
                      attribute.options.map((option) => (
                        <span
                          key={option}
                          className="rounded-full bg-primary/10 px-3 py-1 text-xs text-primary"
                        >
                          {option}
                        </span>
                      ))
                    ) : (
                      <span className="text-xs text-slate-400">No options</span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-slate-400">No attributes linked to this template.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default TemplatesDetailPanel;
